/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, May 23rd 2022, 12:20:22 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import {
  schemaBuilder,
  Many2OneUUIDPKBaseSchema,
  Many2ManyIntPKBaseSchema,
  Many2ManyUUIDPKBaseSchema,
  validateManyAtLeastOne,
} from "lib/validation";
import { Department, Portal, Team } from "lib/schemas";
import { UUID4 } from "lib/types";
import { User, USER_TYPES } from "./User";

export enum INVITATION_TYPES {
  SELF_INVITATION = "SELF_INVITATION",
  INVITED_BY_USER = "INVITED_BY_USER",
}

export enum INVITATION_STATUS {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  CANCELLED = "CANCELLED",
}

export interface UserInvitation {
  invitee_emails: string[];
  readonly invitee_email?: string;
  invited_as: keyof typeof USER_TYPES;
  inviter: User["id"];
  invitee?: User["id"];
  readonly invitation_token?: string;
  readonly invitation_type?: keyof typeof INVITATION_TYPES;
  readonly invitation_status?: keyof typeof INVITATION_STATUS;
  readonly invitation_date?: Date;
  readonly date_accepted?: Date;
  organization: UUID4;
  department: Department["id"];
  portals: Portal["id"][];
  readonly portal_urls?: string[];
  acceptance_base_url: string;
  invitee_teams?: Team["id"][];
}

type WriteOnlyUserInvitation = Omit<
  UserInvitation,
  | "invitation_type"
  | "invitation_token"
  | "invitation_status"
  | "invitation_date"
  | "date_accepted"
  | "invitee_email"
  | "portal_urls"
>;

export const UserInvitationValidationSchema: schemaBuilder.SchemaOf<WriteOnlyUserInvitation> = schemaBuilder
  .object()
  .shape({
    invitee_emails: schemaBuilder
      .array(schemaBuilder.string().email("Kindly provide a valid email addresses."))
      .min(1, "Please enter the email addresses of the workers to be invited.")
      .required("Please enter the email addresses of the workers to be invited."),
    invited_as: schemaBuilder
      .mixed()
      .oneOf(Object.keys(USER_TYPES))
      .required("You must indicate whether the user will be an administrator or not."),
    inviter: schemaBuilder.number().required("Inviter id required"),
    invitee: schemaBuilder.number().optional(),
    organization: schemaBuilder
      .string()
      .uuid()
      .required("Please choose the Organization you are inviting this worker to."),
    department: Many2OneUUIDPKBaseSchema()
      .required("Please choose a department to add the invited worker to")
      .typeError("Select a valid department"),
    portals: Many2ManyIntPKBaseSchema().test(
      "at-least-one-portal",
      "You must assign at least one portal to the invited worker.",
      validateManyAtLeastOne
    ),
    invitee_teams: Many2ManyUUIDPKBaseSchema().optional().nullable().typeError("Select valid teams"),
    acceptance_base_url: schemaBuilder.string().url().required("acceptance_base_url is required"),
  });

export type InvitationAcceptance = Pick<User, "first_name" | "last_name"> &
  Required<Pick<UserInvitation, "invitation_token">> & {
    password: string;
    confirm_password: string;
  };

export const InvitationAcceptanceValidationSchema: schemaBuilder.SchemaOf<InvitationAcceptance> = schemaBuilder
  .object()
  .shape({
    invitation_token: schemaBuilder.string().required(),
    first_name: schemaBuilder.string().required("Please enter your first name."),
    last_name: schemaBuilder.string().required("Please enter your last name."),
    password: schemaBuilder
      .string()
      .required("Please enter your password.")
      .min(8, "Password must be at least 8 characters long."),
    confirm_password: schemaBuilder
      .string()
      .required("Please confirm your password.")
      .oneOf([schemaBuilder.ref("password"), null], "Passwords are not matching. Please try again."),
  });
